import { z } from 'zod';
import type { ContractError } from './errors';
import { toContractError } from './errors';
import type { Result } from './result';
import { UuidSchema } from './uuid';

const IsoDateTimeSchema = z.string().datetime({ offset: true });

const ReportingMetadataSchema = z.object({
  freshness: z.string(),
  sample_size: z.number().int().nonnegative(),
  last_aggregated_at: IsoDateTimeSchema.nullish(),
});

const AttemptResultSchema = z.object({
  kind: z.string(),
  score: z.number().int().nullish(),
  duration_ms: z.number().int().nullish(),
});

const AttemptSchema = z.object({
  id: UuidSchema,
  attempt_no: z.number().int(),
  state: z.string(),
  started_at: IsoDateTimeSchema.nullish(),
  ended_at: IsoDateTimeSchema.nullish(),
  result: AttemptResultSchema.nullish(),
});

const SessionResultSchema = z.object({
  kind: z.string(),
  score: z.number().int().nullish(),
  duration_ms: z.number().int().nullish(),
});

const SessionMediaLinkSchema = z.object({
  id: UuidSchema,
  camera_type: z.string(),
  media_role: z.string(),
  content_type: z.string().nullish(),
  object_key: z.string(),
  media_url: z.string().url().nullish(),
});

const PlayerLookupSchema = z.object({
  user_id: UuidSchema,
  email: z.string().email().nullish(),
  display_name: z.string().nullish(),
  session_count: z.number().int().nonnegative(),
  last_session_at: IsoDateTimeSchema.nullish(),
});

export const PlayerLookupResponseSchema = z.object({
  items: z.array(PlayerLookupSchema),
});

const UserOverviewRowSchema = z.object({
  user_id: UuidSchema,
  email: z.string().email().nullish(),
  display_name: z.string().nullish(),
  session_count: z.number().int().nonnegative(),
  attempt_count: z.number().int().nonnegative(),
  last_play_at: IsoDateTimeSchema.nullish(),
  win_count: z.number().int().nonnegative(),
  loss_count: z.number().int().nonnegative(),
});

export const UserOverviewResponseSchema = z.object({
  items: z.array(UserOverviewRowSchema),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  metadata: ReportingMetadataSchema,
});

export const PlayerSessionRowSchema = z.object({
  session_id: UuidSchema,
  user_id: UuidSchema.nullish(),
  user_email: z.string().email().nullish(),
  user_label: z.string().nullish(),
  robot_id: UuidSchema,
  robot_name: z.string().nullish(),
  game_type: z.string().nullish(),
  state: z.string(),
  end_reason: z.string().nullish(),
  session_started_at: IsoDateTimeSchema.nullish(),
  session_ended_at: IsoDateTimeSchema.nullish(),
  ticket_state: z.string().nullish(),
  initial_queue_position: z.number().int().nullish(),
  queued_at: IsoDateTimeSchema.nullish(),
  offered_at: IsoDateTimeSchema.nullish(),
  confirmed_at: IsoDateTimeSchema.nullish(),
  queue_wait_ms: z.number().int().nullish(),
  attempt_count: z.number().int().nonnegative(),
  attempts: z.array(AttemptSchema).nullish(),
  session_result: SessionResultSchema.nullish(),
  media: z.array(SessionMediaLinkSchema).nullish(),
});

export const PlayerSessionsResponseSchema = z.object({
  items: z.array(PlayerSessionRowSchema),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  metadata: ReportingMetadataSchema,
});

const DistributionSchema = z.object({
  q1: z.number().nullish(),
  q2: z.number().nullish(),
  q3: z.number().nullish(),
  q4: z.number().nullish(),
  q5: z.number().nullish(),
  p95: z.number().nullish(),
});

const ResultRateSchema = z.object({
  kind: z.string(),
  count: z.number().int().nonnegative(),
  rate: z.number(),
});

export const AggregateSummaryResponseSchema = z.object({
  window_start: IsoDateTimeSchema.nullish(),
  window_end: IsoDateTimeSchema.nullish(),
  sessions_started: z.number().int().nonnegative(),
  sessions_completed: z.number().int().nonnegative(),
  total_attempts: z.number().int().nonnegative(),
  attempts_per_session: DistributionSchema,
  session_result_rates: z.array(ResultRateSchema),
  attempt_result_rates: z.array(ResultRateSchema),
  queue_wait: z.object({
    average_ms: z.number().nullish(),
    distribution: DistributionSchema,
  }),
  metadata: ReportingMetadataSchema,
});

const SessionEventForwardLatencyBucketsSchema = z.object({
  le_100ms: z.number().int().nonnegative(),
  le_250ms: z.number().int().nonnegative(),
  le_500ms: z.number().int().nonnegative(),
  le_1000ms: z.number().int().nonnegative(),
  gt_1000ms: z.number().int().nonnegative(),
});

const SessionEventStreamLagBucketsSchema = z.object({
  le_1000ms: z.number().int().nonnegative(),
  le_5000ms: z.number().int().nonnegative(),
  le_10000ms: z.number().int().nonnegative(),
  le_30000ms: z.number().int().nonnegative(),
  gt_30000ms: z.number().int().nonnegative(),
});

const SessionEventForwardingEventTypeSchema = z.object({
  event_type: z.string(),
  forwarded_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
});

const PushDeliveryOutcomeCountSchema = z.object({
  outcome: z.string(),
  count: z.number().int().nonnegative(),
});

const PushDeliveryStatusCountSchema = z.object({
  status: z.string(),
  count: z.number().int().nonnegative(),
});

const PushDeliveryNotificationKindCountSchema = z.object({
  kind: z.string(),
  count: z.number().int().nonnegative(),
});

const PushDeliveryProviderStatusCountSchema = z.object({
  provider_status_code: z.number().int(),
  count: z.number().int().nonnegative(),
});

export const SessionEventForwardingHealthResponseSchema = z.object({
  generated_at: IsoDateTimeSchema,
  processed_total: z.number().int().nonnegative(),
  forwarded_total: z.number().int().nonnegative(),
  forward_failed_total: z.number().int().nonnegative(),
  retries_total: z.number().int().nonnegative(),
  last_forward_duration_ms: z.number().int().nonnegative().nullish(),
  avg_forward_duration_ms: z.number().nonnegative().nullish(),
  max_forward_duration_ms: z.number().int().nonnegative().nullish(),
  last_failed_forward_duration_ms: z.number().int().nonnegative().nullish(),
  last_stream_lag_ms: z.number().int().nonnegative().nullish(),
  avg_stream_lag_ms: z.number().nonnegative().nullish(),
  max_stream_lag_ms: z.number().int().nonnegative().nullish(),
  last_pickup_wait_ms: z.number().int().nonnegative().nullish(),
  avg_pickup_wait_ms: z.number().nonnegative().nullish(),
  max_pickup_wait_ms: z.number().int().nonnegative().nullish(),
  last_core_api_handler_ms: z.number().int().nonnegative().nullish(),
  avg_core_api_handler_ms: z.number().nonnegative().nullish(),
  max_core_api_handler_ms: z.number().int().nonnegative().nullish(),
  last_ws_emit_ms: z.number().int().nonnegative().nullish(),
  avg_ws_emit_ms: z.number().nonnegative().nullish(),
  max_ws_emit_ms: z.number().int().nonnegative().nullish(),
  last_forwarded_at: IsoDateTimeSchema.nullish(),
  last_failure_at: IsoDateTimeSchema.nullish(),
  last_processed_at: IsoDateTimeSchema.nullish(),
  last_event_type: z.string().nullish(),
  last_stream: z.string().nullish(),
  last_stream_id: z.string().nullish(),
  last_status_code: z.number().int().nullish(),
  last_error_type: z.string().nullish(),
  last_error_message: z.string().nullish(),
  forward_latency_buckets: SessionEventForwardLatencyBucketsSchema,
  stream_lag_buckets: SessionEventStreamLagBucketsSchema,
  event_type_breakdown: z.array(SessionEventForwardingEventTypeSchema),
});

export const PushDeliveryHealthResponseSchema = z.object({
  generated_at: IsoDateTimeSchema,
  worker_last_seen_at: IsoDateTimeSchema.nullish(),
  worker_stale_after_seconds: z.number().int().nonnegative(),
  worker_is_stale: z.boolean(),
  processed_total: z.number().int().nonnegative(),
  last_processed_at: IsoDateTimeSchema.nullish(),
  last_kind: z.string().nullish(),
  last_outcome: z.string().nullish(),
  last_stream: z.string().nullish(),
  last_stream_id: z.string().nullish(),
  last_lag_ms: z.number().int().nonnegative().nullish(),
  outcome_counts: z.array(PushDeliveryOutcomeCountSchema),
  queued_jobs: z.number().int().nonnegative(),
  pending_jobs: z.number().int().nonnegative(),
  oldest_job_age_ms: z.number().int().nonnegative().nullish(),
  recent_window_minutes: z.number().int().positive(),
  recent_deliveries_total: z.number().int().nonnegative(),
  recent_attempted_total: z.number().int().nonnegative(),
  recent_sent_total: z.number().int().nonnegative(),
  recent_failed_total: z.number().int().nonnegative(),
  recent_invalid_subscription_total: z.number().int().nonnegative(),
  recent_pending_total: z.number().int().nonnegative(),
  recent_success_rate: z.number().min(0).max(1).nullish(),
  last_sent_at: IsoDateTimeSchema.nullish(),
  last_failed_at: IsoDateTimeSchema.nullish(),
  last_invalid_subscription_at: IsoDateTimeSchema.nullish(),
  status_breakdown: z.array(PushDeliveryStatusCountSchema),
  notification_kind_breakdown: z.array(PushDeliveryNotificationKindCountSchema),
  provider_status_breakdown: z.array(PushDeliveryProviderStatusCountSchema),
});

const AcquisitionSummarySchema = z.object({
  window_start: IsoDateTimeSchema.nullish(),
  window_end: IsoDateTimeSchema.nullish(),
  challenges_prepared: z.number().int().nonnegative(),
  share_initiated_count: z.number().int().nonnegative(),
  landing_touches: z.number().int().nonnegative(),
  unique_visitors: z.number().int().nonnegative(),
  auth_claims: z.number().int().nonnegative(),
  queue_claims: z.number().int().nonnegative(),
  session_claims: z.number().int().nonnegative(),
  attempt_claims: z.number().int().nonnegative(),
  landing_to_auth_rate: z.number().min(0).max(1).nullish(),
  landing_to_queue_rate: z.number().min(0).max(1).nullish(),
  landing_to_attempt_rate: z.number().min(0).max(1).nullish(),
});

const AcquisitionCampaignRowSchema = z.object({
  utm_source: z.string().nullish(),
  utm_medium: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  utm_id: z.string().nullish(),
  landing_touches: z.number().int().nonnegative(),
  unique_visitors: z.number().int().nonnegative(),
  auth_claims: z.number().int().nonnegative(),
  queue_claims: z.number().int().nonnegative(),
  session_claims: z.number().int().nonnegative(),
  attempt_claims: z.number().int().nonnegative(),
  landing_to_attempt_rate: z.number().min(0).max(1).nullish(),
});

const AcquisitionChallengeRowSchema = z.object({
  challenge_id: UuidSchema,
  public_id: z.string(),
  challenge_url: z.string().url(),
  created_at: IsoDateTimeSchema,
  robot_name: z.string().nullish(),
  game_type_key: z.string().nullish(),
  share_kind: z.string(),
  share_initiated_count: z.number().int().nonnegative(),
  landing_touches: z.number().int().nonnegative(),
  unique_visitors: z.number().int().nonnegative(),
  auth_claims: z.number().int().nonnegative(),
  queue_claims: z.number().int().nonnegative(),
  session_claims: z.number().int().nonnegative(),
  attempt_claims: z.number().int().nonnegative(),
  landing_to_attempt_rate: z.number().min(0).max(1).nullish(),
});

export const AcquisitionOverviewResponseSchema = z.object({
  summary: AcquisitionSummarySchema,
  campaigns: z.array(AcquisitionCampaignRowSchema),
  challenges: z.array(AcquisitionChallengeRowSchema),
  metadata: ReportingMetadataSchema,
});

export type PlayerLookup = z.infer<typeof PlayerLookupSchema>;
export type PlayerLookupResponse = z.infer<typeof PlayerLookupResponseSchema>;
export type UserOverviewRow = z.infer<typeof UserOverviewRowSchema>;
export type UserOverviewResponse = z.infer<typeof UserOverviewResponseSchema>;
export type PlayerSessionRow = z.infer<typeof PlayerSessionRowSchema>;
export type PlayerSessionsResponse = z.infer<typeof PlayerSessionsResponseSchema>;
export type AggregateSummaryResponse = z.infer<typeof AggregateSummaryResponseSchema>;
export type SessionEventForwardingHealthResponse = z.infer<
  typeof SessionEventForwardingHealthResponseSchema
>;
export type PushDeliveryHealthResponse = z.infer<typeof PushDeliveryHealthResponseSchema>;
export type AcquisitionOverviewResponse = z.infer<
  typeof AcquisitionOverviewResponseSchema
>;

const parseContract = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  raw: unknown,
): Result<z.infer<TSchema>, ContractError> => {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: 'http' }),
    };
  }
  return { ok: true, value: parsed.data };
};

export const parsePlayerLookupResponse = (raw: unknown) =>
  parseContract(PlayerLookupResponseSchema, raw);

export const parseUserOverviewResponse = (raw: unknown) =>
  parseContract(UserOverviewResponseSchema, raw);

export const parsePlayerSessionsResponse = (raw: unknown) =>
  parseContract(PlayerSessionsResponseSchema, raw);

export const parsePlayerSessionRow = (raw: unknown) =>
  parseContract(PlayerSessionRowSchema, raw);

export const parseAggregateSummaryResponse = (raw: unknown) =>
  parseContract(AggregateSummaryResponseSchema, raw);

export const parseSessionEventForwardingHealthResponse = (raw: unknown) =>
  parseContract(SessionEventForwardingHealthResponseSchema, raw);

export const parsePushDeliveryHealthResponse = (raw: unknown) =>
  parseContract(PushDeliveryHealthResponseSchema, raw);

export const parseAcquisitionOverviewResponse = (raw: unknown) =>
  parseContract(AcquisitionOverviewResponseSchema, raw);
